import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { HelpCircle, LineChart, Upload } from 'lucide-react'
import {
  allConsultationsSettled,
  type ConsultationListItem,
  type ConsultationStatus,
  listConsultations,
} from '@/api/consultations'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'
import { useAuthStore } from '@/stores/auth'

export const Route = createFileRoute('/seller/dashboard/')({
  component: SellerDashboard,
})

const POLL_INTERVAL_MS = 5_000

const STATUS_LABEL: Record<ConsultationStatus, string> = {
  uploaded: 'Загружена',
  queued: 'В очереди',
  transcribing: 'Расшифровка',
  analyzing: 'Анализ',
  done: 'Готова',
  failed: 'Ошибка',
}

const STATUS_TONE: Record<ConsultationStatus, string> = {
  uploaded: 'bg-muted text-muted-foreground',
  queued: 'bg-muted text-muted-foreground',
  transcribing: 'bg-accent text-accent-foreground',
  analyzing: 'bg-accent text-accent-foreground',
  done: 'bg-primary text-primary-foreground',
  failed: 'bg-destructive text-destructive-foreground',
}

function SellerDashboard() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  // RoleGuard на /seller уже гарантирует user !== null, но делаем fallback на
  // случай рассинхрона store после прямого навигирования.
  const displayName = user?.fullName ?? 'продавец'

  const query = useQuery({
    queryKey: ['consultations', 'list'],
    queryFn: listConsultations,
    // Пока хотя бы одна запись ещё в обработке — опрашиваем бэк каждые 5 с
    // (plan §6.3). Как только все done/failed, поллинг останавливается.
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data || data.items.length === 0) return POLL_INTERVAL_MS
      return allConsultationsSettled(data.items) ? false : POLL_INTERVAL_MS
    },
    refetchOnWindowFocus: true,
  })

  const items = query.data?.items ?? []

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6 pb-24">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          Здравствуйте, {displayName}!
        </h1>
        <p className="text-sm text-muted-foreground">
          Загружайте записи консультаций и смотрите их разбор.
        </p>
      </header>

      <button
        type="button"
        onClick={() => navigate({ to: '/seller/analytics' })}
        className="flex min-h-12 items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-3">
          <LineChart className="h-5 w-5 text-primary" aria-hidden="true" />
          <span className="flex flex-col">
            <span className="font-medium text-foreground">Моя динамика</span>
            <span className="text-xs text-muted-foreground">
              Среднее по чек-листу, тренды тегов и зоны роста за период
            </span>
          </span>
        </span>
        <span aria-hidden="true" className="text-muted-foreground">
          →
        </span>
      </button>

      <button
        type="button"
        onClick={() => navigate({ to: '/seller/guide' })}
        className="flex min-h-12 items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-3">
          <HelpCircle className="h-5 w-5 text-primary" aria-hidden="true" />
          <span className="flex flex-col">
            <span className="font-medium text-foreground">Как пользоваться</span>
            <span className="text-xs text-muted-foreground">
              Короткая инструкция: запись, загрузка, разбор
            </span>
          </span>
        </span>
        <span aria-hidden="true" className="text-muted-foreground">
          →
        </span>
      </button>

      <Card>
        <CardHeader>
          <CardTitle>Мои записи</CardTitle>
          <CardDescription>
            {query.isLoading
              ? 'Загружаем…'
              : items.length === 0
                ? 'Записей пока нет'
                : `Всего записей: ${items.length}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {query.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {describeErrorMessage(query.error, 'Не удалось загрузить список.')}
            </p>
          ) : items.length === 0 && !query.isLoading ? (
            <p className="text-sm text-muted-foreground">
              Нажмите «Загрузить» в правом нижнем углу, чтобы отправить первую запись.
            </p>
          ) : (
            <ul aria-label="Список консультаций" className="flex flex-col gap-2">
              {items.map((it) => (
                <ConsultationRow key={it.id} item={it} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <button
        type="button"
        aria-label="Загрузить запись"
        onClick={() => navigate({ to: '/seller/upload' })}
        className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] z-40 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:right-6 sm:h-14 sm:w-14"
      >
        <Upload className="h-7 w-7 sm:h-6 sm:w-6" aria-hidden="true" />
      </button>
    </main>
  )
}

interface ConsultationRowProps {
  item: ConsultationListItem
}

function ConsultationRow({ item }: ConsultationRowProps) {
  const navigate = useNavigate()
  const date = formatConsultationDate(item.consultationDate)
  return (
    <li>
      <button
        type="button"
        onClick={() => navigate({ to: '/seller/consultations/$id', params: { id: item.id } })}
        className="flex min-h-12 w-full items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5 text-left transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex flex-col text-sm">
          <span className="font-medium">{date}</span>
          {item.audioDurationSec ? (
            <span className="text-xs text-muted-foreground">
              {formatDuration(item.audioDurationSec)}
            </span>
          ) : null}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[item.status]}`}
        >
          {STATUS_LABEL[item.status]}
        </span>
      </button>
    </li>
  )
}

function formatConsultationDate(iso: string): string {
  // ISO YYYY-MM-DD → русская форма «20 апреля 2026». Без Intl.DateTimeFormat
  // дёшево достаточно: всё ISO и одна локаль.
  const [y, m, d] = iso.split('-').map((n) => Number.parseInt(n, 10))
  if (!y || !m || !d) return iso
  const months = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ]
  return `${d} ${months[m - 1]} ${y}`
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m} мин ${s.toString().padStart(2, '0')} с`
}
